import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { RecipientsFieldProps } from '@/types/predefined-messages';

import { Button } from '@/components/ui/button';
import { FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  areAllRecipientsSelected,
  resetPrioritizedIfPriorityRecipientRemoved,
  toggleAllRecipientsInPredefinedMessageForm,
  toggleRecipientIdInPredefinedMessageForm,
} from '@/lib/helpers/predefined-messages';
import { getRoleDisplayName } from '@/lib/helpers/role-mapper';
import {
  getRoleBadgeClassName,
  getRoleBadgeHoverClassName,
  getRoleBadgeSelectedHoverClassName,
} from '@/lib/helpers/role-variants';
import { cn } from '@/lib/utils';

export function RecipientsField({
  register,
  control,
  getValues,
  setValue,
  recipients,
  recipientsError,
}: RecipientsFieldProps) {
  const { t } = useTranslation();
  const allRoleId = 'all';
  const recipientIds = useWatch({ control, name: 'recipientIds' }) ?? [];

  const allRecipientIds = useMemo(
    () => recipients.filter(({ id }) => id !== allRoleId).map(({ id }) => id),
    [recipients]
  );

  const recipientsAllDisabled = allRecipientIds.length === 0;

  const allRecipientsSelected = areAllRecipientsSelected({
    allRecipientIds,
    recipientIds,
  });

  function handleRecipientClick(id: string) {
    if (id === allRoleId) {
      toggleAllRecipientsInPredefinedMessageForm({
        setValue,
        allRecipientIds,
        allCurrentlySelected: allRecipientsSelected,
      });
    } else {
      toggleRecipientIdInPredefinedMessageForm({ getValues, setValue, id });
    }

    resetPrioritizedIfPriorityRecipientRemoved({ getValues, setValue });
  }

  const isSelected = (id: string) =>
    id === allRoleId ? allRecipientsSelected : recipientIds.includes(id);

  const isAllSelected = (id: string) => id === allRoleId;

  const getRecipientClassName = (id: string, shortName: string | null) => {
    if (isSelected(id)) {
      return cn(
        getRoleBadgeClassName(shortName),
        getRoleBadgeSelectedHoverClassName(shortName),
        'border border-transparent'
      );
    }

    return cn('hover:text-black hover:border-border', getRoleBadgeHoverClassName(shortName));
  };

  const recipientSelectionOption = (id: string, shortName: string | null) =>
    cn(
      'rounded-full',
      getRecipientClassName(id, shortName),
      id === allRoleId && recipientsAllDisabled && 'opacity-40'
    );

  return (
    <div className="space-y-2">
      <FieldLabel className="text-sm font-medium" required>
        {t('configuration.predefinedMessages.columns.recipients')}
      </FieldLabel>
      <Input type="hidden" {...register('recipientIds')} />
      <div className="flex flex-wrap gap-2">
        {recipients.map(({ id, shortName, label }) => {
          return (
            <div className="flex gap-2" key={id}>
              <Button
                size="sm"
                type="button"
                variant={isSelected(id) ? 'default' : 'outline'}
                disabled={id === allRoleId && recipientsAllDisabled}
                onClick={() => handleRecipientClick(id)}
                className={recipientSelectionOption(id, shortName)}
              >
                {shortName ? getRoleDisplayName(shortName, t) : label}
              </Button>
              {isAllSelected(id) && (
                <Separator orientation="vertical" className="bg-border h-[65%]! self-center" />
              )}
            </div>
          );
        })}
      </div>
      {recipientsError && (
        <p className="text-destructive text-xs" role="alert">
          {recipientsError.message}
        </p>
      )}
    </div>
  );
}
